"use client";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium relative overflow-hidden transition-all duration-300 ease-in-out focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 [&_svg]:transition-all [&_svg]:duration-300 [&_svg]:ease-in-out",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground shadow-sm hover:shadow-elevated hover:translate-y-[-2px] [&_svg]:hover:rotate-3 group",
        destructive:
          "bg-destructive text-destructive-foreground shadow-sm hover:shadow-elevated hover:translate-y-[-2px] [&_svg]:hover:rotate-3 group",
        outline:
          "border border-input bg-background shadow-sm hover:text-accent-foreground hover:border-accent hover:shadow-elevated hover:translate-y-[-2px] [&_svg]:hover:rotate-3 group",
        secondary:
          "bg-secondary text-secondary-foreground shadow-sm hover:shadow-elevated hover:translate-y-[-2px] [&_svg]:hover:rotate-3 group",
        ghost: "hover:shadow-sm hover:translate-y-[-2px] [&_svg]:hover:rotate-3 group",
        link: "text-primary underline-offset-4 hover:underline [&_svg]:hover:translate-x-1 group"
      },
      size: {
        default: "h-9 px-4 py-2",
        sm: "h-8 rounded-md px-3 text-xs",
        lg: "h-10 rounded-md px-8",
        icon: "h-9 w-9 [&_svg]:hover:scale-110 [&_svg]:hover:rotate-0 hover:[&_svg]:animate-pulse"
      }
    },
    defaultVariants: {
      variant: "default",
      size: "default"
    }
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    const [isRippling, setIsRippling] = React.useState(false);
    const [ripplePosition, setRipplePosition] = React.useState({ x: 0, y: 0 });

    // Define the gradient colors based on variant
    const gradientClasses = {
      default: "bg-gradient-to-br from-primary/80 to-primary/40",
      destructive: "bg-gradient-to-br from-destructive/80 to-destructive/40",
      secondary: "bg-gradient-to-br from-secondary/80 to-secondary/40",
      outline: "bg-gradient-to-br from-accent/20 to-accent/5",
      ghost: "bg-gradient-to-br from-accent/20 to-accent/5",
      link: ""
    };

    const glowClasses = {
      default: "shadow-[0_0_15px_rgba(var(--primary)/0.6)]",
      destructive: "shadow-[0_0_15px_rgba(var(--destructive)/0.6)]",
      secondary: "shadow-[0_0_15px_rgba(var(--secondary)/0.6)]",
      outline: "shadow-[0_0_10px_rgba(var(--accent)/0.3)]",
      ghost: "shadow-[0_0_10px_rgba(var(--accent)/0.3)]",
      link: ""
    };

    const handleMouseDown = (e: React.MouseEvent<HTMLButtonElement>) => {
      if (variant === "link" || props.disabled) return;

      const button = e.currentTarget;
      const rect = button.getBoundingClientRect();

      setRipplePosition({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      });

      setIsRippling(true);

      // Reset ripple after animation completes
      setTimeout(() => {
        setIsRippling(false);
      }, 600);

      // Call original onMouseDown if it exists
      props.onMouseDown?.(e);
    };

    // When asChild is true, we need to handle it differently
    // because Slot expects a single child
    if (asChild) {
      return (
        <Comp
          className={cn(buttonVariants({ variant, size, className }))}
          ref={ref}
          {...props}
          onMouseDown={handleMouseDown}
          data-oid=":4j91l3"
        >
          {props.children}
        </Comp>
      );
    }

    // Regular button implementation with effects
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
        onMouseDown={handleMouseDown}
        data-oid=":4j91l3"
      >
        {props.children}

        {/* Gradient overlay */}
        {variant !== "link" && (
          <span
            className={cn(
              "group-hover:animate-pulse-subtle pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-500 ease-in-out group-hover:opacity-100",
              gradientClasses[variant || "default"]
            )}
          />
        )}

        {/* Glow effect */}
        {variant !== "link" && (
          <span
            className={cn(
              "pointer-events-none absolute inset-0 rounded-md opacity-0 transition-all duration-500 ease-in-out group-hover:opacity-100",
              glowClasses[variant || "default"]
            )}
          />
        )}

        {/* Shimmer effect - using the loading-shimmer class from globals.css */}
        {variant !== "link" && (
          <span className="loading-shimmer pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-300 ease-in-out group-hover:opacity-30" />
        )}

        {/* Ripple effect - using the existing ripple animation from globals.css */}
        {isRippling && variant !== "link" && (
          <span
            className="pointer-events-none absolute rounded-full bg-white/20"
            style={{
              left: ripplePosition.x,
              top: ripplePosition.y,
              width: "5px",
              height: "5px",
              animation: "ripple 0.6s ease-out",
              transformOrigin: "50% 50%"
            }}
          />
        )}
      </Comp>
    );
  }
);
Button.displayName = "Button";

export { Button, buttonVariants };
